import { useMutation } from '@apollo/client';
import {
  Button,
  Divider,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AiFillCamera } from 'react-icons/ai';
import * as yup from 'yup';
import cms from '../../../apis/cms';
import {
  CREATE_GROUP_ELEMENT,
  UPDATE_GROUP_ELEMENT,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Input from '../../Form/Input';
import Select from '../../Form/Select';
import Upload from '../../Form/Upload';
import RedirectForm, { redirectFormSchema } from '../../FormGroup/RedirectForm';
import context from '../context';

export const weightOptions = [
  {
    label: '100',
    value: 100,
  },
  {
    label: '200',
    value: 200,
  },
  {
    label: '300',
    value: 300,
  },
  {
    label: '400',
    value: 400,
  },
  {
    label: '500',
    value: 500,
  },
  {
    label: '600',
    value: 600,
  },
  {
    label: '700',
    value: 700,
  },
  {
    label: '800',
    value: 800,
  },
  {
    label: '900',
    value: 900,
  },
];

const imageAlignOptions = [
  {
    label: 'Esquerda',
    value: 'left',
  },
  {
    label: 'Direita',
    value: 'right',
  },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (item: any) => void;
  onEdited?: (item: any) => void;
  onDelete?: () => void;
  Item?: null | any;
}

interface FormData {
  name?: string;
  redirect_value:
    | string
    | {
        label: string | number;
        value: string | number;
      };
  redirect_type: {
    label: string | number;
    value: string | number;
  };
  weight?: {
    label: string | number;
    value: string | number;
  };
  facets?: string;
  file_id?: string;
  file?: FileList;
  image_align?: {
    label: string | number;
    value: string | number;
  };
}

const ItemForm: React.FC<Props> = ({
  onClose,
  isOpen,
  Item,
  onCreated,
  onEdited,
}) => {
  const [createGroupElement] = useMutation(CREATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const [updateGroupElement] = useMutation(UPDATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const { pageGroup, element } = useContext(context);

  const createFormData = yup
    .object()
    .shape({
      name: ['with-image', 'with-search'].includes(pageGroup?.type)
        ? yup.string().nullable()
        : yup.string().required('Título é obrigatório'),
      image_align: yup.object().when({
        is: () => pageGroup?.type !== 'default',
        then: yup.object().shape({
          value: yup
            .string()
            .oneOf(['left', 'right'], 'Escolha um valor válido')
            .required('Escolha o alinhamento do ícone'),
          label: yup.string().required('Escolha o alinhamento do ícone'),
        }),
        otherwise: yup.object().optional().default(undefined),
      }),
      redirect_data: yup.mixed().nullable(),
    })
    .concat(redirectFormSchema);

  const form = useForm({
    resolver: yupResolver(createFormData),
  });
  const { formState, handleSubmit, register, setValue, reset, control } = form;

  const toast = useToast();
  const { errors } = formState;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!Item) return;
    setValue('name', Item.name);
    setValue(
      'weight',
      weightOptions.find(weight => weight?.value === Item?.weight),
    );
    setValue(
      'image_align',
      imageAlignOptions.find(
        imageAlign => imageAlign?.value === Item?.image_align,
      ),
    );
  }, [Item, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const redirectValue: string =
        typeof data?.redirect_value === 'object'
          ? (data?.redirect_value?.value as string)
          : data?.redirect_value;

      if (
        !data?.file?.[0] &&
        !Item?.file?.id &&
        pageGroup?.type === 'with-image'
      ) {
        toast({
          title: 'Opss!!',
          description: 'Adicione uma imagem para continuar',
          status: 'error',
          position: 'top-right',
        });
        return;
      }

      let fileReturn;
      if (data?.file && data?.file[0]) {
        const multipart = new FormData();
        multipart.append('file', data.file[0]);
        const { data: FileApi } = await cms.rest.post<any>(
          '/upload',
          multipart,
        );
        fileReturn = FileApi;
      }

      const { ...rest } = data;

      delete rest.file;
      setLoading(true);
      if (!Item) {
        const groupNewElement = await createGroupElement({
          variables: {
            createGroupElementInput: {
              ...rest,
              page_group_id: pageGroup?.id,
              name: data?.name ?? '',
              redirect_type: data.redirect_type.value,
              redirect_data: undefined,
              weight: data?.weight?.value ?? null,
              file_id: fileReturn ? fileReturn?.id : null,
              redirect_value: redirectValue,
              facets: data.facets,
              element_id: element?.id,
              image_align: data?.image_align?.value ?? 'left',
            },
          },
        });

        if (onCreated) onCreated(groupNewElement?.data?.createGroupElement);
        toast({
          title: 'Sucesso',
          description: 'Página criado com sucesso',
          status: 'success',
          position: 'top-right',
        });
      } else {
        const groupNewElement = await updateGroupElement({
          variables: {
            updateGroupElementInput: {
              ...rest,
              id: Item?.id,
              redirect_data: undefined,
              name: data?.name ?? '',
              page_group_id: pageGroup?.id,
              element_id: element?.id,
              redirect_type: data.redirect_type.value,
              weight: data?.weight?.value ?? null,
              file_id: fileReturn ? fileReturn?.id : Item.file?.id,
              redirect_value: redirectValue,
              facets: data.facets,
              image_align: data?.image_align?.value ?? 'left',
            },
          },
        });
        if (onEdited)
          onEdited({
            ...rest,
            ...groupNewElement?.data?.updateGroupElement,
            redirect_type: data.redirect_type.value,
            redirect_value: redirectValue,
          });
        toast({
          title: 'Sucesso',
          description: 'Item editado com sucesso',
          status: 'success',
          position: 'top-right',
        });
      }
      onClose();
      reset();
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description:
          err?.response?.data?.message ||
          'Tivemos um erro, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
      reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose();
        reset();
      }}
      isOpen={isOpen}
      isCentered
      size="xl"
    >
      <ModalOverlay />
      <ModalContent
        overflowY="auto"
        maxH="80vh"
        as="form"
        onSubmit={handleSubmit(handleForm)}
      >
        <ModalHeader>Link</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={8}>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Tooltip
                label={
                  pageGroup?.type === 'with-image'
                    ? 'O valor á direita utilizado será um ícone ">", você pode alterar isso pelo campo tipo do formulário anterior, setando o campo como "Normal"'
                    : ''
                }
              >
                <Input
                  isDisabled={pageGroup?.type !== 'default'}
                  isRequired={pageGroup?.type === 'default'}
                  label="Título do link"
                  error={errors.name}
                  maxLength={25}
                  {...register('name')}
                />
              </Tooltip>
              <Select
                name="weight"
                control={control}
                label="Peso da fonte"
                error={errors.weight}
                options={weightOptions}
                onChange={() => {
                  setValue('weight', '');
                }}
              />
            </SimpleGrid>
            <Divider />
            <SimpleGrid spacing="8" w="100%">
              {['with-image', 'with-search'].includes(pageGroup?.type) && (
                <Flex>
                  <Upload
                    mb="3"
                    label="Icone"
                    alignSelf="center"
                    icon={AiFillCamera}
                    {...register('file')}
                    error={errors.file}
                    url={
                      Item?.file?.id || Item?.file?.url
                        ? `${Item?.file?.url}`
                        : ''
                    }
                    idFileApi={Item?.file?.id}
                  />
                  <Select
                    name="image_align"
                    control={control}
                    isRequired
                    label="Alinhamento do ícone"
                    error={errors.image_align}
                    options={imageAlignOptions}
                    onChange={() => {
                      setValue('image_align', '');
                    }}
                  />
                </Flex>
              )}
            </SimpleGrid>
            <RedirectForm pageElement={Item} {...form} />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={4}>
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Fechar
            </Button>
            <Button colorScheme="brand" isLoading={loading} type="submit">
              Salvar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ItemForm;
